import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Seo from '@/components/feature/Seo';
import ArticleCard from '@/components/feature/ArticleCard';
import ArticleBody from '@/pages/article/components/ArticleBody';
import { articles, getArticleBySlug } from '@/mocks/articles';
import { getCategory } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug = '' } = useParams();
  const article = getArticleBySlug(slug);
  const [activeId, setActiveId] = useState('');

  const headings = article
    ? article.content
        .map((block, index) => ({ block, index }))
        .filter((item) => item.block.type === 'h2')
    : [];

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  useEffect(() => {
    if (headings.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: '-120px 0px -70% 0px', threshold: 0 },
    );
    headings.forEach((item) => {
      const el = document.getElementById(`h-${item.index}`);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1 flex items-center justify-center px-4 pt-20">
          <div className="text-center py-24">
            <i className="ri-file-unknow-line text-5xl text-foreground-300" aria-hidden="true" />
            <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
              没有找到这篇档案
            </h1>
            <p className="mt-3 text-sm text-foreground-600">它可能已被合并或链接已失效。</p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line" aria-hidden="true" />
              返回首页
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const category = getCategory(article.category);
  const related = articles
    .filter((item) => item.category === article.category && item.slug !== article.slug)
    .concat(articles.filter((item) => item.category !== article.category))
    .slice(0, 3);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      image: article.cover,
      datePublished: article.date,
      dateModified: article.date,
      author: { '@type': 'Organization', name: '反差现场' },
      publisher: { '@type': 'Organization', name: '反差现场' },
      articleSection: article.categoryName,
      keywords: article.tags.join(','),
      inLanguage: 'zh-CN',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
        { '@type': 'ListItem', position: 2, name: article.categoryName, item: `/category/${article.category}` },
        { '@type': 'ListItem', position: 3, name: article.title, item: `/article/${article.slug}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Seo
        title={`${article.title}｜反差现场`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},网红反差,网红反差台上台下`}
        path={`/article/${article.slug}`}
        image={article.cover}
        jsonLd={jsonLd}
      />
      <SiteHeader />

      <main className="flex-1 pt-16 md:pt-20">
        <article>
          <div className="bg-background-100/60 border-b border-background-200">
            <div className="mx-auto max-w-content px-4 md:px-8 py-8 md:py-12">
              <nav aria-label="面包屑" className="flex items-center flex-wrap gap-2 text-xs text-foreground-500 font-label">
                <Link to="/" className="hover:text-primary-600">首页</Link>
                <i className="ri-arrow-right-s-line" aria-hidden="true" />
                <Link to={`/category/${article.category}`} className="hover:text-primary-600">
                  {article.categoryName}
                </Link>
                <i className="ri-arrow-right-s-line" aria-hidden="true" />
                <span className="text-foreground-800 line-clamp-1 max-w-[220px] md:max-w-none">
                  {article.title}
                </span>
              </nav>

              <div className="mt-5 flex items-center gap-3">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-label hover:bg-secondary-200 transition-colors"
                >
                  {article.categoryName}
                </Link>
                <span className="text-xs text-foreground-500 font-label">{article.date}</span>
              </div>

              <h1 className="mt-4 font-heading text-2xl md:text-4xl font-black leading-snug text-foreground-950 max-w-4xl">
                {article.title}
              </h1>

              <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600 max-w-3xl">
                {article.excerpt}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500 font-label">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line" aria-hidden="true" />
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-timer-line" aria-hidden="true" />
                  阅读约 {article.readTime} 分钟
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line" aria-hidden="true" />
                  {article.views.toLocaleString('zh-CN')} 次浏览
                </span>
              </div>
            </div>
          </div>

          <div className="mx-auto max-w-content px-4 md:px-8 py-10 md:py-14">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
              <div className="lg:col-span-8">
                <figure className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200 mb-8">
                  <img
                    src={article.cover}
                    alt={article.coverAlt}
                    title={`${article.title} - 反差现场`}
                    className="w-full h-full object-cover object-top"
                  />
                  <figcaption className="sr-only">{article.coverAlt}</figcaption>
                </figure>

                <section className="rounded-lg border border-background-200 overflow-hidden mb-10">
                  <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-background-200">
                    <div className="p-5 md:p-6 bg-primary-50">
                      <p className="flex items-center gap-2 text-xs tracking-[0.2em] text-primary-700 font-label mb-3">
                        <i className="ri-live-line" aria-hidden="true" />
                        台上 / 镜头前
                      </p>
                      <p className="text-sm leading-relaxed text-foreground-800">{article.onstage}</p>
                    </div>
                    <div className="p-5 md:p-6 bg-secondary-50">
                      <p className="flex items-center gap-2 text-xs tracking-[0.2em] text-secondary-700 font-label mb-3">
                        <i className="ri-eye-off-line" aria-hidden="true" />
                        台下 / 镜头外
                      </p>
                      <p className="text-sm leading-relaxed text-foreground-800">{article.offstage}</p>
                    </div>
                  </div>
                </section>

                <ArticleBody blocks={article.content} />

                <div className="mt-10 flex flex-wrap items-center gap-2 pt-6 border-t border-background-200">
                  <span className="text-xs text-foreground-500 font-label mr-1">相关标签</span>
                  {article.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/category/${article.category}`}
                      className="px-3 py-1.5 rounded-full bg-background-100 border border-background-200 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              </div>

              <aside className="lg:col-span-4">
                <div className="lg:sticky lg:top-24 space-y-6">
                  {headings.length > 0 && (
                    <nav aria-label="内容目录" className="bg-background-100 border border-background-200 rounded-lg p-5">
                      <h2 className="flex items-center gap-2 font-heading text-sm text-foreground-950 mb-4">
                        <i className="ri-list-check-2 text-primary-500" aria-hidden="true" />
                        内容目录
                      </h2>
                      <ul className="space-y-3">
                        {headings.map((item) => {
                          const id = `h-${item.index}`;
                          const text = item.block.type === 'h2' ? item.block.text : '';
                          return (
                            <li key={id}>
                              <a
                                href={`#${id}`}
                                className={`block text-sm leading-snug transition-colors border-l-2 pl-3 ${
                                  activeId === id
                                    ? 'border-primary-500 text-primary-600'
                                    : 'border-background-300 text-foreground-600 hover:text-foreground-900'
                                }`}
                              >
                                {text}
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    </nav>
                  )}

                  {category && (
                    <div className="bg-secondary-900 rounded-lg p-5">
                      <p className="text-xs tracking-[0.2em] text-accent-300 font-label mb-2">本栏目</p>
                      <h2 className="font-heading text-lg text-background-50">{category.name}</h2>
                      <p className="mt-2 text-sm leading-relaxed text-secondary-200">{category.desc}</p>
                      <Link
                        to={`/category/${category.slug}`}
                        className="mt-4 inline-flex items-center gap-1.5 text-sm text-accent-300 hover:text-accent-200 font-label"
                      >
                        查看栏目全部内容
                        <i className="ri-arrow-right-line" aria-hidden="true" />
                      </Link>
                    </div>
                  )}
                </div>
              </aside>
            </div>
          </div>
        </article>

        <section className="bg-background-100/70 border-t border-background-200">
          <div className="mx-auto max-w-content px-4 md:px-8 py-12 md:py-16">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
              相关推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {related.map((item) => (
                <ArticleCard key={item.id} article={item} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}